import React from 'react';
import { dehydrate, QueryClient } from 'react-query';
import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import { type AttributeDefinitionFragment } from '@generated/graphql';
import { getFeatureFlagValue as getFeatureFlagValueAbby } from '@libs/abby/abby';
import { createKetteModel, KetteEinbauArt, product2Kette } from '@hooks/states/echainConfigSlice';
import { EchainProductStoreProvider } from '@hooks/states/useEchainProductStore';
import { ProductPageStoreProvider } from '@hooks/states/useProductPageStore';
import { useHasDynamicPrice } from '@hooks/useHasDynamicPrice';
import { AssociationType } from '@libs/associations';
import { fetchBreadcrumbItems } from '@libs/breadcrumb.helper';
import {
  CATEGORY_ENTRYPOINT_KEY,
  DEFAULT_CURRENCY,
  getLocale,
  PRIMARY_CATEGORY,
  QUERY_KEYS,
  REQUIRED_PRODUCT_PAGE_ATTRIBUTES,
  VAT_CATEGORIES,
} from '@libs/constants';
import { getCurrencyForPriceCountry } from '@libs/currencies';
import {
  fetchAssociatedModelsWithProductsByIds,
  getCurrentProduct,
  getJsonLd,
  getModelCategoriesData,
  getReleasedAndSellableProducts,
} from '@libs/graphql.helper.server';
import { getProductLanguageAlternates, getServerSideTranslations } from '@libs/i18n.helper';
import {
  determineProductUnitSystem,
  findInternationalProductSku,
  getProductCmsId,
  getProductMetaDescription,
  getProductPriceType,
  getProductTitle,
  removeUnnecessaryAttributes,
} from '@libs/product.helper';
import { getDeliveryTimeLocale } from '@libs/utils';
import { getReadOnlyVariantAxes, getSelectableVariantAxes } from '@libs/variant-axes.helper';
import { checkIfApplicationExamplesExist } from '@page-helpers/checkIfApplicationExamplesExist';
import { getProductPageContent } from '@page-helpers/getProductPageContent';
import { getProductAttributeDefinitions } from '@page-helpers/getProductAttributeDefinitions';
import { getAlternativeProductAssociatedModelIds } from '@product/ProductPage/PageLayouts/getAlternativeProductAssociatedModelIds';
import { getAssociatedModelIdsFromProducts } from '@product/ProductPage/PageLayouts/getAssociatedModelIdsFromProducts';
import type { ExtendedProductPageProps } from '@product/ProductPage/ProductPage';
import { ProductPage as ProductPageComponent } from '@product/ProductPage/ProductPage';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getJsonLdValues } from '@page-helpers/getJsonLdValues';
import { Logger } from '@icommerce/logger';
import { getPriceCountry } from '@libs/get-price-country.helper';
import { isLegacyHeaderData } from '@layout/Header';
import type { MinMaxLengthConfig } from '@libs/units';
import { MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG } from '@libs/units';
import type { GraphQlPriceType } from '@models/graphql';
import type { NextRuntimeConfiguration } from '@libs/config';
import getConfig from 'next/config';
import { getDataFromCache, setDataToCache } from '@icommerce/cache-fetch/src/utils/cacheHandler';
import type { CacheFetchOptions } from '@icommerce/cache-fetch';
import { getCategory } from '@libs/category.helper.server';
import { getAttributeValue } from '@libs/attribute.helper';

const { serverRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

const cacheOptions: CacheFetchOptions = {
  cache: {
    provider: 'redis',
    expireTtl: serverRuntimeConfig.graphqlApi.expireTtl,
  },
  updateTtl: serverRuntimeConfig.graphqlApi.updateTtl,
  timeoutMs: serverRuntimeConfig.graphqlApi.timeoutMs,
};

/**
 * Called on server side to fetch data which is required to render the page at build time.
 *
 * @param context The current context
 */
export const getStaticProps: GetStaticProps = async context => {
  const queryClient = new QueryClient();
  const startTime = Date.now();
  const locale = getLocale(context);
  const deliveryTimeLocale = getDeliveryTimeLocale(locale);
  const priceCountry = getPriceCountry(locale);
  const currency = getCurrencyForPriceCountry(priceCountry) || DEFAULT_CURRENCY;

  // Get modelId from the URL
  const modelKey = context.params?.product as string;

  const cacheKey = `${modelKey}_${locale}_${priceCountry}_${deliveryTimeLocale}_${currency}`;

  const response = await getDataFromCache<ExtendedProductPageProps>([cacheKey], cacheOptions);

  const translations = await getServerSideTranslations(serverSideTranslations, locale, [
    'layout/breadcrumb',
    'layout/header',
    'layout/contactBox',
    'layout/contactPersonBox',
    'toast/toast',
    'product/buyBox',
    'category/productTeaserPriceBox',
    'category/productTeaser',
    'product/echainBuyBox',
    'product/copyToClipboard',
    'product/decisionToolsBox',
    'product/productDetails',
    'product/productMediaCarousel',
    'product/cfCaseSelectorModalContent',
    'product/eSkinInformation',
    'product/deliveryInfo',
    'product/eChainExpertTeaserBox',
    'product/mobileMainMenu',
    'layout/localeSelection',
    'layout/shopInfo',
    'layout/userConfirmationModal',
    'layout/general',
    'layout/footer',
  ]);

  if (response) {
    const { data: cachedProps, isOutdated } = response;
    if (cachedProps && !isOutdated) {
      Logger.info(
        {
          modelId: modelKey,
          locale,
          priceCountry,
          duration: Date.now() - startTime,
        },
        'ProductPageRequestFinished: with cache'
      );
      return { props: { ...cachedProps, ...translations }, revalidate: serverRuntimeConfig.staticSiteCacheTtl };
    }
  }

  // #region model data
  const modelCategoriesData = await getModelCategoriesData(modelKey);
  if (!modelCategoriesData) {
    return { notFound: true, revalidate: serverRuntimeConfig.staticSiteCacheTtl };
  }
  const { modelCategories, family_code } = modelCategoriesData;
  // #endregion
  // #region Get list of all released and sellable products for the model
  const { products: availableProducts, variant_axes } = await getReleasedAndSellableProducts(
    modelKey,
    locale,
    deliveryTimeLocale
  );
  if (!availableProducts || availableProducts.length === 0) {
    return { notFound: true, revalidate: serverRuntimeConfig.staticSiteCacheTtl };
  }
  // #endregion

  // Get SKU for the current product based on the query parameter
  const selectedVariantSku = findInternationalProductSku(availableProducts, null);

  const currentProduct = await getCurrentProduct({
    productId: selectedVariantSku,
    country: priceCountry,
    currency,
    deliveryTimeLocale,
    locale,
  });

  const title = getProductTitle(currentProduct);

  // #region generate Breadcrumb
  let categoryCode = modelCategories[0]?.category_code;
  if (currentProduct.attributes.main_category_key) {
    const mainCategoryKey = getAttributeValue<string>(currentProduct.attributes.main_category_key);
    if (modelCategories.find(mc => mc.category_code === mainCategoryKey)) {
      categoryCode = mainCategoryKey;
    }
  }

  if (!categoryCode) {
    Logger.warn(
      {
        message: `Could not get categoryCode because model has no category. This is a data issue`,
        modelKey,
        locale,
      },
      'categoryCodeMissing'
    );
    return { notFound: true, revalidate: serverRuntimeConfig.staticSiteCacheTtl };
  }

  const categoryTree = await getCategory(CATEGORY_ENTRYPOINT_KEY, locale, categoryCode);

  const breadcrumbItems = await fetchBreadcrumbItems(categoryCode, locale, family_code);

  const isVatCategory = !!breadcrumbItems.find(item => VAT_CATEGORIES.includes(item?.key));

  if (
    // temporary featureFlag for PO/BU testing on webtest
    !getFeatureFlagValueAbby('showProductPageAlsoForUnreleasedCategory') &&
    (!breadcrumbItems || breadcrumbItems.length === 0)
  ) {
    return { notFound: true, revalidate: serverRuntimeConfig.staticSiteCacheTtl };
  }

  breadcrumbItems.push({ title, url: null });
  // #endregion

  // fetch JSON-LD
  const jsonLdProducts = (await getJsonLd(modelKey, locale, priceCountry, currency)) ?? [];

  const [productAttributeDefinitions] = await Promise.all([
    getProductAttributeDefinitions(variant_axes, currentProduct, locale),

    // Prefetch shelves models associations for current E-Chain product only
    categoryCode === PRIMARY_CATEGORY.ECHAIN &&
      (await queryClient.prefetchQuery([QUERY_KEYS.SHELVES_MODELS_ASSOCIATIONS, currentProduct.id], () => {
        const shelvesProductAssociationModelIds = getAssociatedModelIdsFromProducts(
          [currentProduct],
          AssociationType.SHELVES
        );
        return fetchAssociatedModelsWithProductsByIds(
          shelvesProductAssociationModelIds,
          locale,
          priceCountry,
          currency
        );
      })),
  ]);

  const {
    selectableVariantAxesAttributesDefinitions,
    selectableVariantAxesAttributeNames,
    readOnlyVariantAxesAttributesDefinitions,
    readOnlyAttributeNames,
    echainAttributeDefinitions,
    echainAttributeNames,
  } = productAttributeDefinitions as {
    selectableVariantAxesAttributesDefinitions: AttributeDefinitionFragment[];
    selectableVariantAxesAttributeNames: string[];
    readOnlyVariantAxesAttributesDefinitions: AttributeDefinitionFragment[];
    readOnlyAttributeNames: string[];
    echainAttributeDefinitions: AttributeDefinitionFragment[];
    echainAttributeNames: string[];
  };

  const [jsonLdValues, pageContentValues, doApplicationExamplesExist, alternativeModelProductsByModelId] =
    await Promise.all([
      getJsonLdValues(
        jsonLdProducts,
        currentProduct,
        title,
        getProductMetaDescription(currentProduct),
        locale,
        isVatCategory
      ),
      getProductPageContent(currentProduct, modelKey, locale),
      checkIfApplicationExamplesExist(currentProduct, locale, modelKey),
      getAlternativeProductAssociatedModelIds([currentProduct], deliveryTimeLocale, locale, priceCountry),
    ]);

  const { videoJsonLd, productJsonLd } = jsonLdValues;

  const {
    onlineToolsDefinition,
    dailyCopperPriceDefinition,
    footer,
    contact,
    marketingTeasers,
    certificateIcons,
    header: headerWithoutLanguageAlternates,
  } = pageContentValues;

  const languageAlternates = getProductLanguageAlternates(getProductCmsId(currentProduct), modelKey);

  // when it is the legacy header data, we need to add the language alternates to the header
  const header = isLegacyHeaderData(headerWithoutLanguageAlternates)
    ? { ...headerWithoutLanguageAlternates, languageAlternates }
    : headerWithoutLanguageAlternates;

  const selectableVariantAxes = getSelectableVariantAxes(
    availableProducts,
    selectableVariantAxesAttributeNames,
    selectableVariantAxesAttributesDefinitions,
    categoryCode,
    locale,
    currentProduct
  );

  const readOnlyVariantAxes = getReadOnlyVariantAxes(
    availableProducts,
    readOnlyAttributeNames,
    readOnlyVariantAxesAttributesDefinitions,
    categoryCode,
    locale
  );

  const variantAxes = selectableVariantAxes.concat(readOnlyVariantAxes);
  const requiredDynamicAttributeNames = [...echainAttributeNames];
  requiredDynamicAttributeNames.push(...variantAxes.map(value => value.attribute));

  const productPriceType: GraphQlPriceType = getProductPriceType(currentProduct);
  const priceTypeConfig: MinMaxLengthConfig =
    (MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG[productPriceType] as MinMaxLengthConfig) ||
    MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG.default;
  if (priceTypeConfig.minAttribute && priceTypeConfig.maxAttribute) {
    requiredDynamicAttributeNames.push(priceTypeConfig.minAttribute);
    requiredDynamicAttributeNames.push(priceTypeConfig.maxAttribute);
  }

  /*
   * In order to reduce the page size the fetched product data is reduced and unnecessary
   * product attributes are removed.
   * */
  const requiredAttributeNames = REQUIRED_PRODUCT_PAGE_ATTRIBUTES.concat(requiredDynamicAttributeNames);

  removeUnnecessaryAttributes(currentProduct, requiredAttributeNames);

  const props: ExtendedProductPageProps = {
    unitSystem: determineProductUnitSystem(locale, categoryCode),
    dehydratedState: dehydrate(queryClient),
    selectedVariant: selectedVariantSku,
    allProducts: availableProducts,
    currentProduct,
    priceType: productPriceType,
    primaryCategoryCode: categoryCode,
    breadcrumbItems,
    productJsonLd,
    videoJsonLd,
    selectableVariantAxesAttributesDefinitions,
    certificateIcons,
    onlineToolsDefinition,
    dailyCopperPriceDefinition,
    marketingTeasers,
    contact,
    languageAlternates,
    doApplicationExamplesExist,
    modelKey,
    echainAttributeDefinitions,
    alternativeModelProductsByModelId,
    availableVariantAxes: variantAxes,
    header,
    footer,
    locale,
    categoryTree,
  };

  Logger.info(
    {
      modelId: modelKey,
      productId: currentProduct.id,
      locale,
      priceCountry,
      duration: Date.now() - startTime,
    },
    'ProductPageRequestFinished: without cache'
  );
  void setDataToCache(props, [cacheKey], cacheOptions);

  return { props: { ...props, ...translations }, revalidate: serverRuntimeConfig.staticSiteCacheTtl };
};

export const getStaticPaths: GetStaticPaths = (): GetStaticPathsResult => ({
  paths: [],
  fallback: 'blocking',
});

const ProductPage: React.FC<ExtendedProductPageProps> = props => {
  const { currentProduct, primaryCategoryCode } = props;
  const chain =
    primaryCategoryCode === PRIMARY_CATEGORY.ECHAIN ? product2Kette(currentProduct).toModel() : createKetteModel();

  return (
    <EchainProductStoreProvider
      initialData={{
        eChainPartsList: null,
        einbauArt: KetteEinbauArt.freitragend,
        isEdit: false,
        kette: chain,
        loadDynamicPrices: useHasDynamicPrice(),
        systemAmount: 1,
        hasAussermittigeEinspeisung: false,
      }}
    >
      <ProductPageStoreProvider initialData={{ ...props }}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <ProductPageComponent {...props} />
      </ProductPageStoreProvider>
    </EchainProductStoreProvider>
  );
};

export default ProductPage;
